import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { MdDateRange } from "react-icons/md";
import { RiCloseLargeFill } from "react-icons/ri";
import toast from "react-hot-toast";
import Loader from "../../../components/Loader";
import ReportManageServices from "../../../services/ReportManageServices";
import "../../../styles/ReportDateDetailModal.css";

const api = new ReportManageServices();

const fmt = (n) => Number(n ?? 0).toLocaleString("en-IN");

const fmtPL = (v) => {
  const n = Number(v ?? 0);
  if (n >= 0) return fmt(n);
  return `-${fmt(Math.abs(n))}`;
};

const plClass = (v) => (Number(v ?? 0) >= 0 ? "rdd-pl-pos" : "rdd-pl-neg");

function GameMetricCells({ purchase, win, cancel, pl }) {
  return (
    <>
      <td className="rdd-num">{fmt(purchase)}</td>
      <td className="rdd-num">{fmt(win)}</td>
      <td className="rdd-num">{fmt(cancel)}</td>
      <td className={`rdd-num rdd-pl ${plClass(pl)}`}>{fmtPL(pl)}</td>
    </>
  );
}

const userHasPurchase = (user) =>
  Number(user?.game_3min?.purchase || 0) > 0 ||
  Number(user?.game_5min?.purchase || 0) > 0;

const TABLE_COLS = 6;

function UserGameRows({ user, variant = "outlet" }) {
  const label = user.username || user.user_name || `User #${user.user_id}`;
  const gameRows = [];

  if (Number(user.game_3min?.purchase || 0) > 0 || Number(user.game_5min?.purchase || 0) > 0) {
    gameRows.push({
      type: "3min",
      purchase: user.game_3min?.purchase,
      win: user.game_3min?.win,
      cancel: user.game_3min?.cancel,
      pl: user.game_3min?.profit_loss,
    });
  
    gameRows.push({
      type: "5min",
      purchase: user.game_5min?.purchase,
      win: user.game_5min?.win,
      cancel: user.game_5min?.cancel,
      pl: user.game_5min?.profit_loss,
    });
  }

  if (gameRows.length === 0) return null;

  return (
    <tbody className="rdd-user-group">
      {gameRows.map((row, idx) => (
        <tr key={`${user.user_id}-${row.type}`} className="rdd-type-row">
          {idx === 0 && (
            <td
              rowSpan={gameRows.length}
              className={`rdd-user-cell rdd-user-cell-${variant}`}
            >
              <span className="rdd-user-cell-inner">
                <span className="rdd-user-dot" />
                <span className="rdd-user-name">{label}</span>
              </span>
            </td>
          )}
          <td className="rdd-game-type">{row.type}</td>
          <GameMetricCells
            purchase={row.purchase}
            win={row.win}
            cancel={row.cancel}
            pl={row.pl}
          />
        </tr>
      ))}
    </tbody>
  );
}

function GameSummaryStrip({ summary }) {
  const games = [
    {
      key: "3min",
      label: "3min Yantra",
      data: summary.game_3min,
      pl: summary.total_data?.game_3min_profit_loss,
    },
    {
      key: "5min",
      label: "5min Yantra",
      data: summary.game_5min,
      pl: summary.total_data?.game_5min_profit_loss,
    },
  ];
  return (
    <div className="rdd-game-strip">
      {games.map((g) => (
        <div key={g.key} className="rdd-game-card">
          <div className="rdd-game-card-title">{g.label}</div>
          <div className="rdd-game-card-metrics">
            <div>
              <span>Purchase</span>
              <strong>{fmt(g.data?.total_purchase_amount)}</strong>
            </div>
            <div>
              <span>Win</span>
              <strong>{fmt(g.data?.total_win_amount)}</strong>
            </div>
            <div>
              <span>Cancel</span>
              <strong>{fmt(g.data?.total_cancel_amount)}</strong>
            </div>
            <div>
              <span>P/L</span>
              <strong className={plClass(g.pl)}>{fmtPL(g.pl)}</strong>
            </div>
          </div>
        </div>
      ))}
      <div className="rdd-game-card rdd-game-card-total">
        <div className="rdd-game-card-title">Total</div>
        <div className="rdd-game-card-metrics">
          <div>
            <span>Purchase</span>
            <strong>{fmt(summary.total_data?.total_purchase_amount)}</strong>
          </div>
          <div>
            <span>Win</span>
            <strong>{fmt(summary.total_data?.total_win_amount)}</strong>
          </div>
          <div>
            <span>Cancel</span>
            <strong>{fmt(summary.total_data?.total_cancel_amount)}</strong>
          </div>
          <div>
            <span>Overall P/L</span>
            <strong
              className={plClass(summary.total_data?.overall_profit_loss)}
            >
              {fmtPL(summary.total_data?.overall_profit_loss)}
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
}

const ReportDateDetailModal = ({
  show,
  onClose,
  txnDate,
  userId,
  displayUserName,
  loggedInRoleId,
}) => {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!show || !txnDate) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setDetail(null);
      try {
        const res = await api.getReportDateDetail(txnDate, userId);
        if (cancelled) return;
        if (res?.isSuccess) {
          setDetail(res.data);
        } else {
          setDetail(null);
          toast.error(res?.message || "Failed to load day detail");
        }
      } catch {
        if (!cancelled) {
          setDetail(null);
          toast.error("Failed to load day detail");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, txnDate, userId]);

  const summary = detail?.summary;
  const titleName = detail?.target_user_name || displayUserName || "—";
  const hideSuperAdmin = Number(loggedInRoleId) === 1;

  const activeOutletUsers = useMemo(
    () => (detail?.outlet_users || []).filter(userHasPurchase),
    [detail?.outlet_users],
  );
  const activeMobileUsers = useMemo(
    () => (detail?.mobile_users || []).filter(userHasPurchase),
    [detail?.mobile_users],
  );
  const hasBreakdown =
    activeOutletUsers.length > 0 || activeMobileUsers.length > 0;

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="xl"
      className="rdd-modal"
      scrollable
    >
      <div className="rdd-modal-head">
        <div className="rdd-modal-head-inner">
          <MdDateRange size={22} />
          <div>
            <h2 className="rdd-modal-title">
              {txnDate ? moment(txnDate).format("DD MMM YYYY") : "—"}
            </h2>
            <p className="rdd-modal-sub">[{titleName}]</p>
          </div>
        </div>
        <button
          type="button"
          className="rdd-close"
          onClick={onClose}
          aria-label="Close"
        >
          <RiCloseLargeFill size={18} />
        </button>
      </div>

      <Modal.Body className="rdd-modal-body">
        {loading ? (
          <Loader message="Loading day detail…" />
        ) : !detail ? (
          <p className="rdd-empty">No detail available for this date.</p>
        ) : (
          <>
            {summary && (
              <>
                <div className="rdd-balance-bar">
                  <span>
                    Opening Balance:{" "}
                    <strong>{fmt(summary.user_opening_balance)}</strong>
                  </span>
                  <span>
                    Closing Balance:{" "}
                    <strong>{fmt(summary.user_closing_balance)}</strong>
                  </span>
                </div>
                <GameSummaryStrip summary={summary} />
              </>
            )}

            {detail.is_admin_hierarchy && !hasBreakdown && summary && (
              <p className="rdd-no-activity">
                No outlet or mobile users with game purchase on this date.
              </p>
            )}

            {hasBreakdown && (
              <div className="rdd-breakdown">
                <div className="rdd-breakdown-title">
                  User-wise game breakdown
                </div>
                <div className="rdd-table-scroll">
                  <table className="rdd-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Type</th>
                        <th>Purchase</th>
                        <th>Win</th>
                        <th>Cancel</th>
                        <th>P/L</th>
                      </tr>
                    </thead>
                    {activeOutletUsers.length > 0 && (
                      <>
                        <tbody>
                          <tr className="rdd-section-row">
                            <td colSpan={TABLE_COLS}>Outlet users</td>
                          </tr>
                        </tbody>
                        {activeOutletUsers.map((u) => (
                          <UserGameRows
                            key={`o-${u.user_id}`}
                            user={u}
                            variant="outlet"
                          />
                        ))}
                      </>
                    )}
                    {activeMobileUsers.length > 0 && (
                      <>
                        <tbody>
                          <tr className="rdd-section-row rdd-section-mobile">
                            <td colSpan={TABLE_COLS}>Mobile users</td>
                          </tr>
                        </tbody>
                        {activeMobileUsers.map((u) => (
                          <UserGameRows
                            key={`m-${u.user_id}`}
                            user={u}
                            variant="mobile"
                          />
                        ))}
                      </>
                    )}
                    <tfoot>
                      <tr className="rdd-grand-total">
                        <td colSpan={2}>Total</td>
                        <td className="rdd-num">
                          {fmt(detail.grand_total?.purchase)}
                        </td>
                        <td className="rdd-num">
                          {fmt(detail.grand_total?.win)}
                        </td>
                        <td className="rdd-num">
                          {fmt(detail.grand_total?.cancel)}
                        </td>
                        <td
                          className={`rdd-num rdd-pl ${plClass(detail.grand_total?.profit_loss)}`}
                        >
                          {fmtPL(detail.grand_total?.profit_loss)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ReportDateDetailModal;
